import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, Validators } from '@angular/forms';
import { StatisticsService } from '../../../core/services/api-services/statistics.service';
import { PageEvent, PaginatorComponent } from "../../../shared/components/paginator/paginator.component";
import { Subject, Subscription, takeUntil } from 'rxjs';
import { BreadcrumbItem, BreadcrumbComponent } from '../../../shared/components/breadcrumb/breadcrumb.component';
import { BreadcrumbService } from '../../../shared/components/breadcrumb/service/breadcrumb.service';
import { FilterPanelComponent } from "../../../shared/components/filter-panel/filter-panel.component";
import { FilterField, FilterResult } from '../../../shared/components/filter-panel/model/filter-field.model';
import { ToastrService } from 'ngx-toastr';

// ── NEW: shape for a grouped salary cycle (one entry per salaryCycleId) ──
interface GroupedCycle {
  salaryCycleId: number;
  salaryCycleName: string;
  totalWithdrawals: number;      // sum across all instances
  currentInstance: any;          // most-recent instance (index 0 after sort)
  instances: any[];              // all 6 instances, newest-first
}

@Component({
  selector: 'app-wage-withdrawals',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginatorComponent, BreadcrumbComponent, FilterPanelComponent],
  templateUrl: './wage-withdrawals.component.html',
  styleUrl: './wage-withdrawals.component.css'
})
export class WageWithdrawalsComponent implements OnInit, OnDestroy {

  breadcrumbItems: BreadcrumbItem[] = [];
  private destroy$ = new Subject<void>();

  dataSubscriber$!: Subscription;

  dashboard: any = null;

  // ───────────────────────────────────────────────────────────────
  // Companies (totals) + pagination + search
  // ───────────────────────────────────────────────────────────────
  isCompaniesLoading = false;
  companiesError = '';
  companies: any[] = [];
  filteredCompanies: any[] = [];
  pagedCompanies: any[] = [];
  companySearchText = '';

  companiesPage = 1;
  companiesPageSize = 10;
  companiesPageSizeOptions = [5, 10, 25, 50];

  // company-level expansion state
  expandedCompanyIds = new Set<number>();

  // ───────────────────────────────────────────────────────────────
  // Grouped cycles per company (lazy, cached)
  // Key: companyId  →  Value: GroupedCycle[]
  // ───────────────────────────────────────────────────────────────
  cyclesByCompany = new Map<number, GroupedCycle[]>();
  isCyclesLoadingMap: Record<number, boolean> = {};
  cyclesErrorMap: Record<number, string> = {};

  // ── NEW: cycle-level expansion state
  // Key format: "<companyId>_<salaryCycleId>"
  expandedCycleKeys = new Set<string>();

  // ───────────────────────────────────────────────────────────────
  // Withdrawals (single offcanvas)
  // ───────────────────────────────────────────────────────────────
  selectedCompany: any = null;
  selectedCycle: any = null;
  // ── NEW: the specific instance whose → was clicked
  selectedInstance: any = null;

  isWithdrawalsLoading = false;
  withdrawalsError = '';
  withdrawals: any[] = [];
  filteredWithdrawals: any[] = [];
  pagedWithdrawals: any[] = [];

  withdrawalsPage = 1;
  withdrawalsPageSize = 10;
  withdrawalsPageSizeOptions = [5, 10, 25, 50];

  withdrawalSearchText = '';

  startDate: string = '';
  endDate: string = '';
  todayYMD: string = '';

  // Companies — server-side
  companiesCurrentPage = 1;
  totalCompanies = 0;

  // Withdrawals — server-side
  withdrawalsCurrentPage = 1;
  totalWithdrawals = 0;

  companyFilterValue = '';
  withdrawalFilterValue = '';

  // =============== Server filter Props =============== //

  filterFields: FilterField[] = [
    {
      type: 'radio-group',
      controlName: 'filterType',
      label: 'Filter By:',
      defaultValue: 'CompanyId',
      options: [
        {
          value: 'CompnayId',
          label: 'Compnay ID',
          field: {
            type: 'text',
            controlName: 'CompanyId',            // key sent to backend
            placeholder: 'Enter Company ID',
            hint: 'Example: 9412265199 (10 digits)',
            validators: [Validators.required, Validators.pattern(/^\d{10}$/), Validators.minLength(10), Validators.maxLength(10)],
          },
        }
      ],
    },
  ];  
  
  withdrawalsFilterFields: FilterField[] = [
    {
      type: 'radio-group',
      controlName: 'filterType',
      label: 'Filter By:',
      defaultValue: 'EmployeeId',
      options: [
        {
          value: 'EmployeeId',
          label: 'Employee ID',
          field: {
            type: 'text',
            controlName: 'EmployeeId',            // key sent to backend
            placeholder: 'Enter Employee ID',
            hint: 'Example: EMP0001',
            validators: [Validators.required],
          },
        }
      ],
    },
  ];

  // =================================================== //

  constructor(
    private statisticsService: StatisticsService, 
    private breadcrumbService: BreadcrumbService,
    private toaster: ToastrService) {
    this.dataSubscriber$ = new Subscription();
  }

  ngOnInit(): void {
    this.breadcrumbService.breadcrumbs$
      .pipe(takeUntil(this.destroy$))
      .subscribe(items => { this.breadcrumbItems = items; });

    this.breadcrumbService.setBreadcrumbs([
      { label: 'Dashboard', url: '/dashboard', icon: 'bi bi-house-door' },
      { label: 'Treasury', icon: 'bi bi-bank' },
      { label: 'Wage Withdrawals', icon: 'bi bi-cash-coin' }
    ]);

    this.todayYMD = this.formatYMD(new Date());
    this.fetchCompanies(this.companiesCurrentPage, this.companiesPageSize);
    this.fetchDashboardStats();
  }

  fetchDashboardStats(): void {
    this.dataSubscriber$.add(
      this.statisticsService.getDashboardStats().subscribe({
        next: (response: any) => {
          if (response?.success) this.dashboard = response.data ?? null;
        },
        error: () => { }
      })
    );
  }

  // ───────────────────────────────────────────────────────────────
  // Companies
  // ───────────────────────────────────────────────────────────────

  fetchCompanies(page: number, size: number, filterValue?: string): void {
    this.isCompaniesLoading = true;
    this.companiesError = '';
    this.dataSubscriber$.add(
      this.statisticsService.getCompanyEwwTotalsPaginated(page, size, filterValue).subscribe({
        next: (response) => {
          if (response.success) {
            this.companies = (response.data ?? []) as any[];
            this.filteredCompanies = [...this.companies];
            this.pagedCompanies = [...this.companies];
            this.totalCompanies = response.totalCount;
            this.companiesCurrentPage = response.pageNumber;
            this.companiesPageSize = response.pageSize;
            this.companiesError = '';
          } else {
            this.companiesError = 'Failed to load companies.';
          }
          this.isCompaniesLoading = false;
        },
        error: (err) => {
          this.companiesError = 'API error: ' + err.message;
          this.isCompaniesLoading = false;
        }
      })
    );
  }

  performCompanySearch(): void {
    const q = (this.companySearchText || '').trim().toLowerCase();
    this.filteredCompanies = !q
      ? [...this.companies]
      : this.companies.filter(c =>
        this.toStr(c.companyName).toLowerCase().includes(q) ||
        this.toStr(c.companyId).toLowerCase().includes(q)
      );
    this.resetCompaniesPagination();
  }

  // ── Company-level expand ──────────────────────────────────────
  isExpanded(companyId: number): boolean {
    return this.expandedCompanyIds.has(companyId);
  }

  toggleExpand(company: any): void {
    const id = Number(company.companyId);
    if (this.isExpanded(id)) {
      this.expandedCompanyIds.delete(id);
      return;
    }
    this.expandedCompanyIds.add(id);
    if (!this.cyclesByCompany.has(id)) {
      this.loadCyclesForCompany(id);
    }
  }

  onCompanyRowClick(c: any): void { this.toggleExpand(c); }
  onCompanyRowKeydown(ev: KeyboardEvent, c: any): void {
    const key = ev.key?.toLowerCase();
    if (key === 'enter' || key === ' ') { ev.preventDefault(); this.toggleExpand(c); }
  }

  // ── NEW: Cycle-level expand ───────────────────────────────────
  cycleKey(companyId: number, salaryCycleId: number): string {
    return `${companyId}_${salaryCycleId}`;
  }

  isCycleExpanded(companyId: number, salaryCycleId: number): boolean {
    return this.expandedCycleKeys.has(this.cycleKey(companyId, salaryCycleId));
  }

  toggleCycleExpand(event: Event, companyId: number, salaryCycleId: number): void {
    event.stopPropagation();
    const key = this.cycleKey(companyId, salaryCycleId);
    if (this.expandedCycleKeys.has(key)) {
      this.expandedCycleKeys.delete(key);
    } else {
      this.expandedCycleKeys.add(key);
    }
  }

  // ── Load & group cycle instances from new API ─────────────────
  private loadCyclesForCompany(companyId: number): void {
    this.isCyclesLoadingMap[companyId] = true;
    this.cyclesErrorMap[companyId] = '';

    this.dataSubscriber$.add(
      // ── CHANGED: use new endpoint that returns 6 instances per cycle ──
      this.statisticsService.getCompanyEwwRecentCycleInstancesTotals(companyId, 6).subscribe({
        next: (response: any) => {
          if (response?.success) {
            const flat: any[] = response.data ?? [];
            this.cyclesByCompany.set(companyId, this.groupCycleInstances(flat));
          } else {
            this.cyclesByCompany.set(companyId, []);
            this.cyclesErrorMap[companyId] = response?.message || 'Failed to load salary cycles.';
          }
          this.isCyclesLoadingMap[companyId] = false;
        },
        error: (err) => {
          this.cyclesByCompany.set(companyId, []);
          this.cyclesErrorMap[companyId] = 'API error (cycles): ' + (err?.message ?? err);
          this.isCyclesLoadingMap[companyId] = false;
        }
      })
    );
  }

  // ── NEW: group flat API response into GroupedCycle[] ──────────
  private groupCycleInstances(flat: any[]): GroupedCycle[] {
    const map = new Map<number, GroupedCycle>();

    flat.forEach(item => {
      if (!map.has(item.salaryCycleId)) {
        map.set(item.salaryCycleId, {
          salaryCycleId: item.salaryCycleId,
          salaryCycleName: item.salaryCycleName,
          totalWithdrawals: 0,
          currentInstance: null,
          instances: []
        });
      }
      const group = map.get(item.salaryCycleId)!;

      // ── Deduplicate: skip if same cycleStart already exists ──
      const alreadyExists = group.instances.some(
        i => i.cycleStart === item.cycleStart && i.cycleEnd === item.cycleEnd
      );
      if (alreadyExists) return;

      group.instances.push(item);
      group.totalWithdrawals += item.totalWithdrawals ?? 0;
    });

    map.forEach(group => {
      group.instances.sort((a, b) =>
        new Date(b.cycleStart).getTime() - new Date(a.cycleStart).getTime()
      );
      group.currentInstance = group.instances[0] ?? null;
    });

    return Array.from(map.values());
  }

  // ───────────────────────────────────────────────────────────────
  // Pagination helpers – companies
  // ───────────────────────────────────────────────────────────────
  private updateCompaniesPage(): void {
    if (this.companiesPage < 1) this.companiesPage = 1;
    const start = (this.companiesPage - 1) * this.companiesPageSize;
    this.pagedCompanies = this.filteredCompanies.slice(start, start + this.companiesPageSize);
  }

  private resetCompaniesPagination(): void {
    this.companiesPage = 1;
    this.updateCompaniesPage();
  }

  // onCompaniesPageChange(paginatedData: any[]): void {
  //   this.pagedCompanies = paginatedData;
  // }

  onCompaniesPageChange(event: PageEvent): void {
    this.companiesCurrentPage = event.page;
    this.companiesPageSize = event.pageSize;
    this.fetchCompanies(event.page, event.pageSize, this.companyFilterValue);
  }

  // ───────────────────────────────────────────────────────────────
  // Withdrawals (offcanvas) – now instance-aware
  // ───────────────────────────────────────────────────────────────

  // ── NEW: called when clicking → on an instance row ────────────
  openCycleInstance(event: Event, company: any, cycle: GroupedCycle, instance: any): void {
    event.stopPropagation();
    this.selectedCompany = company;
    this.selectedCycle = cycle;
    this.selectedInstance = instance;

    // Default date range = the instance's own window
    this.startDate = this.formatYMD(new Date(instance.cycleStart));
    const instanceEnd = new Date(instance.cycleEnd);
    const today = new Date();
    this.endDate = this.formatYMD(instanceEnd < today ? instanceEnd : today);

    this.withdrawalSearchText = '';
    this.filteredWithdrawals = [];
    this.resetWithdrawalsPagination();
    this.fetchWithdrawals();
  }

  // ───────────────────────────────────────────────────────────────────────
  // Withdrawals (offcanvas) – Load with server-side pagination & filtering
  // ───────────────────────────────────────────────────────────────────────
  fetchWithdrawals(withdrawalFilterValue?: string | number): void {
    if (!this.selectedCompany || !this.selectedCycle || !this.selectedInstance) return;

    const companyId = Number(this.selectedCompany.companyId);
    const salaryCycleId = Number(this.selectedCycle.salaryCycleId);

    this.isWithdrawalsLoading = true;
    this.withdrawalsError = '';

    const s = (this.startDate || '').trim() || null;
    const e = (this.endDate || '').trim() || null;

    this.dataSubscriber$.add(
      this.statisticsService.getEmployeeEwwsInSalaryCycleInstancePaginated(
        companyId,
        salaryCycleId,
        this.selectedInstance.cycleStart,
        this.selectedInstance.cycleEnd,
        this.withdrawalsCurrentPage,
        this.withdrawalsPageSize,
        withdrawalFilterValue,
        s,
        e
      ).subscribe({
        next: (res) => {
          if (res?.success) {
            const rows = (res.data ?? []) as any[];
            this.withdrawals = rows.sort((a, b) =>
              new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
            );
            this.pagedWithdrawals = [...this.withdrawals];
            this.filteredWithdrawals = [...this.withdrawals];
            this.totalWithdrawals = res.totalCount;
            this.withdrawalsCurrentPage = res.pageNumber;
            this.withdrawalsPageSize = res.pageSize;
            this.withdrawalsError = '';
          } else {
            this.withdrawalsError = res?.message || 'No withdrawals found.';
            this.pagedWithdrawals = [];
            this.totalWithdrawals = 0;
          }
          this.isWithdrawalsLoading = false;
        },
        error: (err) => {
          this.withdrawalsError = 'API error (withdrawals): ' + (err?.message ?? err);
          this.pagedWithdrawals = [];
          this.totalWithdrawals = 0;
          this.isWithdrawalsLoading = false;
        }
      })
    );
  }

  onDateRangeChange(): void {
    if (!this.selectedInstance) return;
    if (this.endDate) {
      const end = new Date(this.endDate);
      const today = new Date(this.todayYMD);
      if (end > today) this.endDate = this.todayYMD;
    }
    if (this.startDate && this.endDate) {
      const s = new Date(this.startDate);
      const e = new Date(this.endDate);
      if (isNaN(s.getTime()) || isNaN(e.getTime())) {
        this.withdrawalsError = 'Invalid date selection.';
        this.withdrawals = [];
        this.performWithdrawalSearch();
        return;
      }
      if (s > e) {
        this.withdrawalsError = 'Start date cannot be after end date.';
        this.withdrawals = [];
        this.performWithdrawalSearch();
        return;
      }
    }
    this.withdrawalsError = '';
    this.fetchWithdrawals();
  }

  performWithdrawalSearch(): void {
    const q = (this.withdrawalSearchText || '').trim().toLowerCase();
    this.filteredWithdrawals = !q
      ? [...this.withdrawals]
      : this.withdrawals.filter(w => Object.values(w).some(v => String(v).toLowerCase().includes(q)));
    this.resetWithdrawalsPagination();
  }

  // ───────────────────────────────────────────────────────────────
  // Pagination helpers – withdrawals
  // ───────────────────────────────────────────────────────────────
  private updateWithdrawalsPage(): void {
    if (this.withdrawalsPage < 1) this.withdrawalsPage = 1;
    const start = (this.withdrawalsPage - 1) * this.withdrawalsPageSize;
    this.pagedWithdrawals = this.filteredWithdrawals.slice(start, start + this.withdrawalsPageSize);
  }

  private resetWithdrawalsPagination(): void {
    this.withdrawalsPage = 1;
    this.updateWithdrawalsPage();
  }

  // onWithdrawalPageChange(paginatedData: any[]): void {
  //   this.pagedWithdrawals = paginatedData;
  // }

  onWithdrawalPageChange(event: PageEvent): void {
    this.withdrawalsCurrentPage = event.page;
    this.withdrawalsPageSize = event.pageSize;
    this.fetchWithdrawals();
  }


  // ───────────────────────────────────────────────────────────────
  // Helpers
  // ───────────────────────────────────────────────────────────────
  private toStr(v: any): string { return v === null || v === undefined ? '' : String(v); }
  private pad2(n: number): string { return n < 10 ? '0' + n : '' + n; }
  private formatYMD(d: Date): string {
    return `${d.getFullYear()}-${this.pad2(d.getMonth() + 1)}-${this.pad2(d.getDate())}`;
  }

  formatDateTime(iso?: string | Date): string {
    if (!iso) return '';
    return new Date(iso).toLocaleString();
  }

  formatName(w: any): string {
    return [this.toStr(w.firstName), this.toStr(w.middleName), this.toStr(w.lastName)]
      .map(x => x.trim()).filter(Boolean).join(' ');
  }

  // =============================================================================================== //
  // Server Side Filtration
  // =============================================================================================== //

  onFilter(result: FilterResult): void {

    const filterValue = result['CompanyId']?.toString().trim();
    this.companyFilterValue = filterValue || '';

    if (!filterValue) {
      this.toaster.warning('Please enter a value to filter', 'Filter');
      return;
    }

    this.companiesCurrentPage = 1;
    this.companySearchText = '';

    this.fetchCompanies(this.companiesCurrentPage, this.companiesPageSize, filterValue);
  }

  onClear(): void {
    this.companiesCurrentPage = 1;
    this.companySearchText = '';
    this.companyFilterValue = '';
    this.companiesError = '';
    this.fetchCompanies(this.companiesCurrentPage, this.companiesPageSize);
  } 
  
  onWithdrawalsFilter(result: FilterResult): void {

    const filterValue = result['EmployeeId']?.toString().trim();
    this.withdrawalFilterValue = filterValue || '';

    if (!filterValue) {
      this.toaster.warning('Please enter a value to filter', 'Filter');
      return;
    }

    this.withdrawalsCurrentPage = 1;
    this.withdrawalSearchText = '';

    this.fetchWithdrawals(filterValue);
  }

  onWithdrawalsFilterClear(): void {
    this.withdrawalsCurrentPage = 1;
    this.withdrawalSearchText = '';
    this.withdrawalFilterValue = '';
    this.withdrawalsError = '';
    this.fetchWithdrawals();
  }

  // =============================================================================================== //

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.dataSubscriber$.unsubscribe();
  }
}